import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ReceiptLong as SaleIcon,
  AssignmentReturn as ReturnIcon,
  Payments as PaymentIcon,
  RequestQuote as QuotationIcon,
  FileDownload as FileDownloadIcon,
  CardGiftcard as LoyaltyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { customerService } from '../../services/customerService';
import salesService from '../../services/salesService';
import creditService from '../../services/creditService';
import { enqueueSnackbar } from 'notistack';
import { formatCurrency } from '../../utils/currencyFormatter';
import { formatDate } from '../../utils/dateFormatter';
import * as XLSX from 'xlsx';
import axios from '../../utils/axios';

const getTodayIso = () => new Date().toISOString().split('T')[0];
const getMonthStartIso = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toDateOnly = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split('T')[0];
};

const getOrderDate = (order) => {
  return toDateOnly(order?.orderDate) || toDateOnly(order?.createdAt);
};

const buildActivities = (salesOrders, creditTransactions, loyaltyReport) => {
  const saleRows = (Array.isArray(salesOrders) ? salesOrders : []).map((order) => {
    const isQuotation = Boolean(order?.isQuotation);
    const isReturned = String(order?.status || '').toUpperCase() === 'RETURNED';
    const orderTypeLabel = isQuotation ? 'QUOTATION' : (isReturned ? 'RETURN' : 'SALE');
    const itemCount = Array.isArray(order?.items) ? order.items.length : 0;
    const ref = order?.orderNumber || `ORD-${order?.id || ''}`;

    return {
      id: `SO-${order?.id || ref}`,
      date: getOrderDate(order),
      type: orderTypeLabel,
      reference: ref,
      description: isQuotation
        ? `Quotation created (${itemCount} items)`
        : (isReturned
          ? `Sales return processed (${itemCount} items)`
          : `Products sold (${itemCount} items)`),
      amount: safeNumber(order?.total),
      source: order
    };
  });

  const creditRows = (Array.isArray(creditTransactions) ? creditTransactions : [])
    .filter((tx) => {
      const txType = String(tx?.transactionType || tx?.transaction_type || '').toUpperCase();
      return txType === 'PAYMENT' || txType === 'OPENING_BALANCE';
    })
    .map((tx) => {
      const txType = String(tx?.transactionType || tx?.transaction_type || '').toUpperCase();
      return {
      id: `CTX-${tx?.id || tx?.referenceId || tx?.reference_id || Math.random()}`,
      date: toDateOnly(tx?.transactionDate || tx?.transaction_date || tx?.createdAt),
      type: txType === 'OPENING_BALANCE' ? 'OPENING_BALANCE' : 'PAYMENT',
      reference: tx?.referenceId || tx?.reference_id || `PAY-${tx?.id || ''}`,
      description: txType === 'OPENING_BALANCE'
        ? 'Opening balance'
        : `Payment received${tx?.paymentMethod ? ` (${tx.paymentMethod})` : ''}`,
      amount: safeNumber(tx?.amount),
      source: tx
    };});

  const loyaltyEarnRows = (Array.isArray(loyaltyReport?.purchaseHistory) ? loyaltyReport.purchaseHistory : [])
    .map((entry) => ({
      id: `LOY-EARN-${entry?.orderId || entry?.orderNumber || Math.random()}`,
      date: toDateOnly(entry?.orderDate),
      type: 'LOYALTY_EARN',
      reference: entry?.orderNumber || '-',
      description: `Loyalty points earned (${entry?.pointsEarned ?? 0} points)`,
      amount: safeNumber(entry?.total),
      source: entry
    }));

  const loyaltyRedeemRows = (Array.isArray(loyaltyReport?.redemptionHistory) ? loyaltyReport.redemptionHistory : [])
    .map((entry) => ({
      id: `LOY-RED-${entry?.id || entry?.reference || Math.random()}`,
      date: toDateOnly(entry?.redeemedAt),
      type: 'LOYALTY_REDEEM',
      reference: entry?.reference || '-',
      description: `Loyalty redeemed (${entry?.pointsRedeemed ?? 0} points, ${entry?.redemptionType || 'REDEEM'})`,
      amount: safeNumber(entry?.amountAdjusted),
      source: entry
    }));

  return [...saleRows, ...creditRows, ...loyaltyEarnRows, ...loyaltyRedeemRows]
    .filter((row) => !!row.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

const CustomerLedgerHistoryPage = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [filters, setFilters] = useState({
    startDate: getMonthStartIso(),
    endDate: getTodayIso()
  });

  useEffect(() => {
    const loadData = async () => {
      if (!customerId) return;

      try {
        setLoading(true);
        const [customerData, salesOrders, creditTransactions, loyaltyResponse] = await Promise.all([
          customerService.getCustomerById(customerId),
          salesService.getSalesOrdersByCustomer(customerId),
          creditService.getCustomerTransactions(customerId),
          axios.get(`/reports/customer-loyalty?customerId=${customerId}`).catch(() => ({ data: null }))
        ]);

        setCustomer(customerData);
        setActivities(buildActivities(salesOrders, creditTransactions, loyaltyResponse?.data));
      } catch (error) {
        console.error('Failed to load customer ledger history:', error);
        enqueueSnackbar('Failed to load customer ledger history', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [customerId]);

  const filteredActivities = useMemo(() => {
    const { startDate, endDate } = filters;
    return activities.filter((row) => {
      const d = row.date || '';
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }, [activities, filters]);

  const summary = useMemo(() => {
    return filteredActivities.reduce(
      (acc, row) => {
        if (row.type === 'SALE') {
          acc.totalSales += row.amount;
          acc.salesCount += 1;
        } else if (row.type === 'QUOTATION') {
          acc.totalQuotations += row.amount;
          acc.quotationCount += 1;
        } else if (row.type === 'RETURN') {
          acc.totalReturns += row.amount;
          acc.returnCount += 1;
        } else if (row.type === 'PAYMENT') {
          acc.totalPayments += row.amount;
          acc.paymentCount += 1;
        } else if (row.type === 'OPENING_BALANCE') {
          acc.totalOpeningBalance += row.amount;
          acc.openingBalanceCount += 1;
        } else if (row.type === 'LOYALTY_EARN') {
          const pts = safeNumber(row.source?.pointsEarned);
          acc.loyaltyEarnPoints += pts;
          acc.loyaltyEarnCount += 1;
        } else if (row.type === 'LOYALTY_REDEEM') {
          const pts = safeNumber(row.source?.pointsRedeemed);
          acc.loyaltyRedeemPoints += pts;
          acc.loyaltyRedeemAmount += safeNumber(row.amount);
          acc.loyaltyRedeemCount += 1;
        }
        return acc;
      },
      {
        totalSales: 0,
        totalQuotations: 0,
        totalReturns: 0,
        totalPayments: 0,
        totalOpeningBalance: 0,
        salesCount: 0,
        quotationCount: 0,
        returnCount: 0,
        paymentCount: 0,
        openingBalanceCount: 0,
        loyaltyEarnPoints: 0,
        loyaltyEarnCount: 0,
        loyaltyRedeemPoints: 0,
        loyaltyRedeemAmount: 0,
        loyaltyRedeemCount: 0
      }
    );
  }, [filteredActivities]);

  useEffect(() => {
    setPage(0);
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredActivities.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredActivities.length, page, rowsPerPage]);

  const pagedActivities = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredActivities.slice(start, start + rowsPerPage);
  }, [filteredActivities, page]);

  const handleExportExcel = () => {
    try {
      const pad6 = (row) => {
        const r = [...row];
        while (r.length < 6) r.push('');
        return r;
      };

      const periodFromLabel = filters.startDate
        ? formatDate(filters.startDate)
        : 'All dates';
      const periodToLabel = filters.endDate
        ? formatDate(filters.endDate)
        : 'All dates';

      const customerSection = [
        pad6(['CUSTOMER LEDGER HISTORY', '', '', '', '', '']),
        pad6(['Customer name', customer?.name || '-', '', '', '', '']),
        pad6(['Phone', customer?.phone || '-', '', '', '', '']),
        pad6(['Email', customer?.email || '-', '', '', '', '']),
        pad6(['Address', customer?.address || '-', '', '', '', '']),
        pad6(['', '', '', '', '', '']),
        pad6(['REPORT PERIOD (FILTERED)', '', '', '', '', '']),
        pad6(['From', periodFromLabel, '', '', '', '']),
        pad6(['To', periodToLabel, '', '', '', '']),
        pad6(['', '', '', '', '', ''])
      ];

      const transactionHeaders = ['S.No', 'Date', 'Activity', 'Reference', 'Description', 'Amount'];
      const transactionRows = filteredActivities.map((row, index) => [
        index + 1,
        formatDate(row.date),
        row.type,
        row.reference || '-',
        row.description || '-',
        safeNumber(row.amount).toFixed(2)
      ]);

      const finalSheetData = [...customerSection, transactionHeaders, ...transactionRows];

      const worksheet = XLSX.utils.aoa_to_sheet(finalSheetData);
      worksheet['!cols'] = [
        { wch: 10 },
        { wch: 14 },
        { wch: 18 },
        { wch: 22 },
        { wch: 48 },
        { wch: 14 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Ledger');

      XLSX.writeFile(
        workbook,
        `customer_ledger_history_${customer?.name || customerId}_${getTodayIso()}.xlsx`
      );

      enqueueSnackbar('Excel exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to export excel:', error);
      enqueueSnackbar('Failed to export excel', { variant: 'error' });
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Customer Ledger History</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={filteredActivities.length === 0}
            >
              Export Excel
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/people/customers')}
            >
              Back to Customers
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>{customer?.name || 'Customer'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {customer?.phone || '-'} {customer?.email ? `| ${customer.email}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customer?.address || '-'}
              </Typography>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.endDate}
                    onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ height: '56px' }}
                    onClick={() => setFilters({ startDate: getMonthStartIso(), endDate: getTodayIso() })}
                  >
                    This Month
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="text"
                    sx={{ height: '56px' }}
                    onClick={() => setFilters({ startDate: '', endDate: '' })}
                  >
                    Clear Filter
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Products Sold</Typography>
                    <Typography variant="h6">{summary.salesCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalSales)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Quotations</Typography>
                    <Typography variant="h6">{summary.quotationCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalQuotations)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Returns</Typography>
                    <Typography variant="h6">{summary.returnCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalReturns)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Payments</Typography>
                    <Typography variant="h6">{summary.paymentCount}</Typography>
                    <Typography variant="body2">{formatCurrency(summary.totalPayments)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Activity</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredActivities.length > 0 ? (
                      pagedActivities.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={
                                row.type === 'SALE'
                                  ? <SaleIcon />
                                  : row.type === 'RETURN'
                                    ? <ReturnIcon />
                                    : row.type === 'QUOTATION'
                                      ? <QuotationIcon />
                                      : (row.type === 'LOYALTY_EARN' || row.type === 'LOYALTY_REDEEM')
                                        ? <LoyaltyIcon />
                                      : <PaymentIcon />
                              }
                              label={row.type}
                              color={
                                row.type === 'SALE'
                                  ? 'primary'
                                  : row.type === 'RETURN'
                                    ? 'warning'
                                    : row.type === 'QUOTATION'
                                      ? 'info'
                                      : (row.type === 'LOYALTY_EARN' || row.type === 'LOYALTY_REDEEM')
                                        ? 'secondary'
                                      : 'success'
                              }
                            />
                          </TableCell>
                          <TableCell>{row.reference || '-'}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No activities found in selected date range.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredActivities.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
              />
            </Paper>
          </>
        )}
      </Box>
    </Layout>
  );
};

export default CustomerLedgerHistoryPage;
